import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { PaginatedResponse, ServiceTicket, ServiceTicketDetail, TicketBoardColumn } from "@/lib/types"

interface TicketFilters {
  search?: string
  status?: string
  priority?: string
  phase_id?: string
  owner_id?: string
  assigned_to_id?: string
  company_id?: string
  ticket_type_id?: string
  created_at_from?: string
  created_at_to?: string
  sort?: string
  cursor?: string
  limit?: number
}

function buildParams(filters: TicketFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useServiceTickets(filters: TicketFilters = {}) {
  return useQuery({
    queryKey: ["service_tickets", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<ServiceTicket>>(`/api/v1/service_tickets${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useServiceTicket(id?: string) {
  return useQuery({
    queryKey: ["service_tickets", id],
    queryFn: () => api.get<{ data: ServiceTicketDetail }>(`/api/v1/service_tickets/${id}`),
    enabled: !!id,
  })
}

export function useServiceTicketsBoard(ticketTypeId?: string) {
  return useQuery({
    queryKey: ["service_tickets_board", ticketTypeId],
    queryFn: () => {
      const qs = ticketTypeId ? `?ticket_type_id=${ticketTypeId}` : ""
      return api.get<{ data: TicketBoardColumn[]; ticket_type: { id: string; name: string } }>(
        `/api/v1/service_tickets/board${qs}`
      )
    },
  })
}

export function useCreateServiceTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: ServiceTicketDetail }>("/api/v1/service_tickets", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_tickets"] })
      qc.invalidateQueries({ queryKey: ["service_tickets_board"] })
    },
  })
}

export function useUpdateServiceTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: ServiceTicketDetail }>(`/api/v1/service_tickets/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_tickets"] })
      qc.invalidateQueries({ queryKey: ["service_tickets_board"] })
    },
  })
}

export function useDeleteServiceTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/service_tickets/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_tickets"] })
      qc.invalidateQueries({ queryKey: ["service_tickets_board"] })
    },
  })
}

export function useChangeTicketPhase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, phase_id }: { id: string; phase_id: string }) =>
      api.post<{ data: ServiceTicketDetail }>(`/api/v1/service_tickets/${id}/change_phase`, { phase_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_tickets"] })
      qc.invalidateQueries({ queryKey: ["service_tickets_board"] })
    },
  })
}

export function useResolveTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, resolution_notes }: { id: string; resolution_notes?: string }) =>
      api.post<{ data: ServiceTicketDetail }>(`/api/v1/service_tickets/${id}/resolve`, { resolution_notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_tickets"] })
      qc.invalidateQueries({ queryKey: ["service_tickets_board"] })
    },
  })
}

export function useCloseTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: ServiceTicketDetail }>(`/api/v1/service_tickets/${id}/close`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service_tickets"] })
      qc.invalidateQueries({ queryKey: ["service_tickets_board"] })
    },
  })
}
